import React, { useCallback, useEffect, useState } from "react";
import {
    RefreshCw,
    Loader2,
    CheckCircle2,
    AlertTriangle,
    HelpCircle,
    History,
    X,
} from "lucide-react";
import {
    fetchCurrentVersion,
    fetchVersionStats,
    forceUpdateVersion,
    fetchUsersByVersionStatus,
} from "../../service/api/orders";
import "../../css/VersionAdmin.css";

const STATS_POLL_MS = 30000;

const STATUS_META = {
    current: { label: "On current version", icon: CheckCircle2, cls: "version-admin-stat-current" },
    outdated: { label: "Outdated", icon: AlertTriangle, cls: "version-admin-stat-outdated" },
    unknown: { label: "Never reported", icon: HelpCircle, cls: "version-admin-stat-unknown" },
};

export default function VersionAdminPage() {
    const [current, setCurrent] = useState(null);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const [confirmingUpdate, setConfirmingUpdate] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [pendingVersion, setPendingVersion] = useState("");

    // ── User list modal state (infinite scroll) ──
    const [modalStatus, setModalStatus] = useState(null); // 'current' | 'outdated' | 'unknown' | null
    const [modalUsers, setModalUsers] = useState([]);
    const [modalPage, setModalPage] = useState(1);
    const [modalTotal, setModalTotal] = useState(0);
    const [modalHasMore, setModalHasMore] = useState(true);
    const [modalLoading, setModalLoading] = useState(false); // initial load
    const [modalLoadingMore, setModalLoadingMore] = useState(false); // subsequent pages

    const loadAll = useCallback(() => {
        Promise.all([fetchCurrentVersion(), fetchVersionStats()])
            .then(([curRes, statsRes]) => {
                if (curRes?.success) setCurrent(curRes.data);
                if (statsRes?.success) setStats(statsRes.data);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        loadAll();
        const interval = setInterval(loadAll, STATS_POLL_MS);
        return () => clearInterval(interval);
    }, [loadAll]);

    const handleRequestUpdate = () => {
        setPendingVersion(current?.version || "");
        setConfirmingUpdate(true);
    };

    const handleConfirmUpdate = () => {
        setUpdating(true);
        forceUpdateVersion({ version: pendingVersion || null })
            .then((res) => {
                if (res?.success) {
                    setCurrent(res.data);
                    loadAll();
                }
            })
            .catch(() => {})
            .finally(() => {
                setUpdating(false);
                setConfirmingUpdate(false);
            });
    };

    // ── Modal handlers ──
    const openStatusModal = (status) => {
        setModalStatus(status);
        setModalUsers([]);
        setModalPage(1);
        setModalTotal(0);
        setModalHasMore(true);
    };

    const closeStatusModal = () => {
        setModalStatus(null);
        setModalUsers([]);
        setModalLoading(false);
        setModalLoadingMore(false);
    };

    useEffect(() => {
        if (!modalStatus) return;

        const isFirstPage = modalPage === 1;
        isFirstPage ? setModalLoading(true) : setModalLoadingMore(true);

        fetchUsersByVersionStatus(modalStatus, modalPage, 30)
            .then((res) => {
                if (!res?.success) return;
                const { data, total, current_page, last_page } = res.data;
                setModalUsers((prev) => (isFirstPage ? data : [...prev, ...data]));
                setModalTotal(total);
                setModalHasMore(current_page < last_page);
            })
            .catch(() => {})
            .finally(() => {
                setModalLoading(false);
                setModalLoadingMore(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalStatus, modalPage]);

    const handleModalScroll = (e) => {
        if (modalLoading || modalLoadingMore || !modalHasMore) return;
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 120) {
            setModalPage((p) => p + 1);
        }
    };

    const adoptionPct = stats?.total_users
        ? Math.round((stats.on_current / stats.total_users) * 100)
        : 0;

    const meta = modalStatus ? STATUS_META[modalStatus] : null;
    const ModalIcon = meta?.icon;

    return (
        <div className="version-admin">
            <div className="version-admin-header">
                <div>
                    <h1>App Version</h1>
                    <p className="version-admin-subtitle">
                        Force clients to refresh settings and cached assets, and track update adoption.
                    </p>
                </div>
                <button className="version-admin-refresh-btn" onClick={loadAll} title="Refresh">
                    <RefreshCw size={15} />
                </button>
            </div>

            {loading ? (
                <div className="version-admin-loading">
                    <Loader2 size={20} className="version-admin-spin" />
                    <span>Loading…</span>
                </div>
            ) : (
                <>
                    {/* ── Current live version card ── */}
                    <div className="version-admin-card version-admin-current">
                        <div className="version-admin-current-top">
                            <div>
                                <span className="version-admin-label">Live version</span>
                                <div className="version-admin-number">{current?.version || "—"}</div>
                            </div>

                            {!confirmingUpdate ? (
                                <button className="version-admin-force-btn" onClick={handleRequestUpdate}>
                                    <RefreshCw size={14} />
                                    Force New Version
                                </button>
                            ) : (
                                <div className="version-admin-confirm">
                                    <input
                                        type="text"
                                        className="version-admin-confirm-input"
                                        placeholder={`Auto-bump from ${current?.version || "1.0.0"}`}
                                        value={pendingVersion}
                                        onChange={(e) => setPendingVersion(e.target.value)}
                                        disabled={updating}
                                    />
                                    <div className="version-admin-confirm-actions">
                                        <button
                                            className="version-admin-cancel-btn"
                                            onClick={() => setConfirmingUpdate(false)}
                                            disabled={updating}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="version-admin-confirm-btn"
                                            onClick={handleConfirmUpdate}
                                            disabled={updating}
                                        >
                                            {updating ? (
                                                <>
                                                    <Loader2 size={13} className="version-admin-spin" />
                                                    Updating…
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle2 size={13} />
                                                    Confirm force update
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="version-admin-keys">
                            <div className="version-admin-key-row">
                                <span>forceNewSettingsVersion</span>
                                <code>{current?.forceNewSettingsVersion || "—"}</code>
                            </div>
                            <div className="version-admin-key-row">
                                <span>forceCacheClearVersion</span>
                                <code>{current?.forceCacheClearVersion || "—"}</code>
                            </div>
                        </div>
                    </div>

                    {/* ── Adoption stats (clickable) ── */}
                    <div className="version-admin-stats-grid">
                        <button
                            type="button"
                            className="version-admin-stat-card version-admin-stat-current version-admin-stat-clickable"
                            onClick={() => openStatusModal("current")}
                        >
                            <CheckCircle2 size={18} />
                            <div>
                                <div className="version-admin-stat-value">{stats?.on_current ?? 0}</div>
                                <div className="version-admin-stat-label">On current version</div>
                            </div>
                        </button>
                        <button
                            type="button"
                            className="version-admin-stat-card version-admin-stat-outdated version-admin-stat-clickable"
                            onClick={() => openStatusModal("outdated")}
                        >
                            <AlertTriangle size={18} />
                            <div>
                                <div className="version-admin-stat-value">{stats?.outdated ?? 0}</div>
                                <div className="version-admin-stat-label">Outdated</div>
                            </div>
                        </button>
                        <button
                            type="button"
                            className="version-admin-stat-card version-admin-stat-unknown version-admin-stat-clickable"
                            onClick={() => openStatusModal("unknown")}
                        >
                            <HelpCircle size={18} />
                            <div>
                                <div className="version-admin-stat-value">{stats?.unknown ?? 0}</div>
                                <div className="version-admin-stat-label">Never reported</div>
                            </div>
                        </button>
                    </div>

                    <div className="version-admin-adoption-bar-wrap">
                        <div className="version-admin-adoption-bar-header">
                            <span>Adoption</span>
                            <span>{adoptionPct}%</span>
                        </div>
                        <div className="version-admin-adoption-bar">
                            <div className="version-admin-adoption-bar-fill" style={{ width: `${adoptionPct}%` }} />
                        </div>
                    </div>

                    {/* ── Breakdown by version ── */}
                    <div className="version-admin-card">
                        <div className="version-admin-breakdown-header">
                            <History size={14} />
                            <span>Version breakdown</span>
                        </div>
                        {(stats?.breakdown ?? []).length === 0 ? (
                            <div className="version-admin-empty">No version data reported yet</div>
                        ) : (
                            <div className="version-admin-breakdown-list">
                                {stats.breakdown.map((row) => (
                                    <div key={row.app_version} className="version-admin-breakdown-row">
                                        <span
                                            className={`version-admin-breakdown-version ${
                                                row.app_version === stats.current_version ? "is-current" : ""
                                            }`}
                                        >
                                            {row.app_version}
                                            {row.app_version === stats.current_version && " (current)"}
                                        </span>
                                        <span className="version-admin-breakdown-count">{row.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ── User list modal ── */}
            {modalStatus && (
                <div className="version-admin-modal-overlay" onClick={closeStatusModal}>
                    <div className="version-admin-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="version-admin-modal-header">
                            <div className={`version-admin-modal-title ${meta.cls}`}>
                                {ModalIcon && <ModalIcon size={16} />}
                                <span>{meta.label}</span>
                                {modalTotal > 0 && <span className="version-admin-modal-total">({modalTotal})</span>}
                            </div>
                            <button className="version-admin-modal-close" onClick={closeStatusModal}>
                                <X size={16} />
                            </button>
                        </div>

                        {modalLoading ? (
                            <div className="version-admin-loading">
                                <Loader2 size={18} className="version-admin-spin" />
                                <span>Loading…</span>
                            </div>
                        ) : modalUsers.length === 0 ? (
                            <div className="version-admin-empty">No users found in this bucket</div>
                        ) : (
                            <div className="version-admin-modal-list" onScroll={handleModalScroll}>
                                <div className="version-admin-modal-row version-admin-modal-row-head">
                                    <span>Name</span>
                                    <span>Contact</span>
                                    <span>Version</span>
                                    <span>Last reported</span>
                                </div>
                                {modalUsers.map((user) => (
                                    <div key={user.id} className="version-admin-modal-row">
                                        <span className="version-admin-modal-user-name">
                                            {user.name || `User #${user.id}`}
                                        </span>
                                        <span className="version-admin-modal-user-meta">
                                            {user.email || user.phone || "—"}
                                        </span>
                                        <span className="version-admin-modal-user-version">
                                            {user.app_version || "—"}
                                        </span>
                                        <span className="version-admin-modal-user-date">
                                            {user.app_version_updated_at
                                                ? new Date(user.app_version_updated_at).toLocaleDateString()
                                                : "—"}
                                        </span>
                                    </div>
                                ))}
                                {modalLoadingMore && (
                                    <div className="version-admin-modal-loading-more">
                                        <Loader2 size={14} className="version-admin-spin" />
                                        <span>Loading more…</span>
                                    </div>
                                )}
                                {!modalHasMore && modalUsers.length > 0 && (
                                    <div className="version-admin-modal-end">End of list</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}